import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Send } from 'lucide-react';
import type { QuoteSend } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';

// Sending the quote, and what came back.
//
// The link is always shown, not just emailed. A quote frequently goes
// out inside a reply an account manager is already writing, and an agent
// who cannot get at the link has to re-send the whole thing to get one.

interface Props {
  ticketId: string;
  /** Lets the editor above lock itself once the quote has been signed. */
  onAcceptedChange?: (accepted: boolean) => void;
  /** The document changed under us — re-read so states stay truthful. */
  reloadKey: number;
}

const STATE_LABELS: Record<QuoteSend['state'], string> = {
  live: 'Awaiting the client',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  superseded: 'Superseded by a newer version',
};

const STATE_CLASSES: Record<QuoteSend['state'], string> = {
  live: 'border-sky-200 bg-sky-50 text-sky-800',
  accepted: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  declined: 'border-rose-200 bg-rose-50 text-rose-800',
  expired: 'border-[var(--color-border)] bg-white text-[var(--color-muted)]',
  superseded: 'border-[var(--color-border)] bg-white text-[var(--color-muted)]',
};

const inputClass =
  'block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

/** `YYYY-MM-DD`, `days` from today. */
function datePlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const EXPIRY_PRESETS: Array<{ value: string; label: string; days: number | null }> = [
  { value: '14', label: '14 days', days: 14 },
  { value: '30', label: '30 days', days: 30 },
  { value: '60', label: '60 days', days: 60 },
  { value: '90', label: '90 days', days: 90 },
  { value: 'custom', label: 'Pick a date…', days: null },
  { value: 'never', label: 'No expiry', days: null },
];

export function QuoteSendPanel({
  ticketId,
  onAcceptedChange,
  reloadKey,
}: Props): React.ReactElement {
  const [sends, setSends] = useState<QuoteSend[]>([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [note, setNote] = useState('');
  const [expiryChoice, setExpiryChoice] = useState('30');
  const [customExpiry, setCustomExpiry] = useState(datePlus(30));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ sends: QuoteSend[] }>(
        `/api/tickets/${ticketId}/quote-document/sends`,
      );
      setSends(data.sends);
      onAcceptedChange?.(data.sends.some((s) => s.state === 'accepted'));
    } catch {
      // A failure here must not take the editor above down with it; the
      // send history is secondary to writing the quote.
      setSends([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId, onAcceptedChange]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  function expiresOn(): string | null {
    if (expiryChoice === 'never') return null;
    if (expiryChoice === 'custom') return customExpiry || null;
    const preset = EXPIRY_PRESETS.find((p) => p.value === expiryChoice);
    return preset?.days ? datePlus(preset.days) : null;
  }

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    setEmailWarning(null);
    try {
      const data = await apiFetch<{ send: QuoteSend; emailSent: boolean; emailError: string | null }>(
        `/api/tickets/${ticketId}/quote-document/send`,
        {
          method: 'POST',
          json: {
            toEmail: toEmail.trim(),
            expiresOn: expiresOn(),
            note: note.trim() || null,
          },
        },
      );
      // The link exists either way, so a mail failure is a warning beside
      // a successful send — not an error that hides the link the agent
      // can now paste into their own email.
      if (!data.emailSent) {
        setEmailWarning(
          `${data.emailError ?? 'The email could not be sent.'} The link below is live — you can send it yourself.`,
        );
      }
      setComposing(false);
      setNote('');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send the quote.');
    } finally {
      setBusy(false);
    }
  }

  async function copy(send: QuoteSend): Promise<void> {
    try {
      await navigator.clipboard.writeText(send.url);
      setCopiedId(send.id);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Could not copy the link. Select it from the box and copy manually.');
    }
  }

  const accepted = sends.some((s) => s.state === 'accepted');
  const latest = sends[0] ?? null;

  if (loading) {
    return <p className="text-sm text-[var(--color-muted)]">Loading send history…</p>;
  }

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
      {emailWarning && (
        <p className="rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {emailWarning}
        </p>
      )}

      {composing ? (
        <div className="space-y-3 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
              Send to
            </span>
            <input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              className={inputClass}
              placeholder="name@client.com"
            />
            <span className="mt-1 block text-xs text-[var(--color-muted)]">
              Anyone holding the link can sign — it does not need to be a portal contact.
            </span>
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Valid for
              </span>
              <select
                value={expiryChoice}
                onChange={(e) => setExpiryChoice(e.target.value)}
                className={inputClass}
              >
                {EXPIRY_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {expiryChoice === 'custom' && (
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                  Expires on
                </span>
                <input
                  type="date"
                  value={customExpiry}
                  min={datePlus(1)}
                  onChange={(e) => setCustomExpiry(e.target.value)}
                  className={inputClass}
                />
              </label>
            )}
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
              Note in the email{' '}
              <span className="font-normal text-[var(--color-muted)]">(optional)</span>
            </span>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={inputClass}
              placeholder="A line of context — the quote itself lives behind the link."
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void send()} disabled={busy || !toEmail.trim()}>
              {busy ? 'Sending…' : latest ? 'Send new version' : 'Send quote'}
            </Button>
            <Button variant="outline" onClick={() => setComposing(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
          {latest && latest.state === 'live' && (
            <p className="text-xs text-[var(--color-muted)]">
              Sending again replaces the link already with the client — the old one will tell them
              the quote was revised.
            </p>
          )}
        </div>
      ) : (
        !accepted && (
          <Button variant="secondary" onClick={() => setComposing(true)}>
            <Send className="h-4 w-4" aria-hidden="true" />
            {latest ? 'Send again' : 'Send to client'}
          </Button>
        )
      )}

      {sends.length === 0 ? (
        !composing && (
          <p className="text-sm text-[var(--color-muted)]">
            This quote has not been sent yet.
          </p>
        )
      ) : (
        <ul className="space-y-3">
          {sends.map((s) => (
            <li
              key={s.id}
              className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${STATE_CLASSES[s.state]}`}
                >
                  {STATE_LABELS[s.state]}
                </span>
                <span className="text-xs text-[var(--color-muted)]">
                  Sent to {s.sentToEmail} on {new Date(s.sentAt).toLocaleDateString()}
                  {s.sentByAgentName ? ` by ${s.sentByAgentName}` : ''}
                </span>
              </div>

              {s.acceptance ? (
                <div className="mt-3 text-sm text-[var(--color-body)]">
                  <p>
                    <strong>
                      {s.acceptance.decision === 'accepted' ? 'Accepted' : 'Declined'} by{' '}
                      {s.acceptance.signerName}
                    </strong>{' '}
                    ({s.acceptance.signerEmail}) on{' '}
                    {new Date(s.acceptance.decidedAt).toLocaleString()}
                  </p>
                  {s.acceptance.declineReason && (
                    <p className="mt-1 whitespace-pre-wrap text-[var(--color-muted)]">
                      Reason: {s.acceptance.declineReason}
                    </p>
                  )}
                  <p className="mt-2 font-mono text-xs text-[var(--color-muted)]">
                    Document reference {s.acceptance.snapshotSha256.slice(0, 16)}
                  </p>
                </div>
              ) : (
                <>
                  {s.expiresOn && (
                    <p className="mt-2 text-xs text-[var(--color-muted)]">
                      Valid until {s.expiresOn}
                    </p>
                  )}
                  {s.state === 'live' && (
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        readOnly
                        value={s.url}
                        onFocus={(e) => e.currentTarget.select()}
                        className={`${inputClass} font-mono text-xs`}
                        aria-label="Client link"
                      />
                      <button
                        type="button"
                        onClick={() => void copy(s)}
                        className="inline-flex shrink-0 items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-t3-blue)] px-3 py-2 text-xs font-semibold text-[var(--color-t3-blue)] hover:bg-[var(--color-t3-blue)]/10"
                      >
                        {copiedId === s.id ? (
                          <>
                            <Check className="h-3.5 w-3.5" aria-hidden="true" /> Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5" aria-hidden="true" /> Copy
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
